import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, CheckCircle, ChevronLeft, ChevronRight, FileText, Heart, MapPin, Minus, Package, Plus, ShieldCheck, ShoppingCart, Star, Store } from 'lucide-react';
import Navbar from '../components/Navbar';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useLocation2 } from '../context/LocationContext';
import { useWishlist } from '../context/WishlistContext';
import { calcDistance } from '../components/LocationMap';
import { ErrorState, LoadingState } from '../components/StateMessage';
import { toast } from 'react-toastify';
import QuoteRequestModal from '../components/QuoteRequestModal';

const CATEGORY_LABELS = {
  sand: 'Sand & Aggregates',
  cement: 'Cement',
  iron: 'Steel',
  bricks: 'Bricks',
  long_bricks: 'Long Bricks',
  short_bricks: 'Short Bricks',
  other: 'Hardware'
};

export default function MaterialDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart } = useCart();
  const { toggle: toggleWishlist, isWishlisted } = useWishlist();
  const { location: userLocation } = useLocation2();
  const [material, setMaterial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeImage, setActiveImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(-1);
  const [reviews, setReviews] = useState([]);
  const [relatedMaterials, setRelatedMaterials] = useState([]);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [showQuote, setShowQuote] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError('');
    API.get(`/materials/${id}`)
      .then(async (materialRes) => {
        const materialData = materialRes.data;
        let reviewsData = null;
        try {
          const reviewsRes = await API.get(`/materials/${id}/reviews`);
          reviewsData = reviewsRes.data;
        } catch (reviewErr) {
          setReviews([]);
        }
        setMaterial({
          ...materialData,
          rating: reviewsData?.rating || materialData?.rating || 0,
          totalRatings: reviewsData?.totalRatings || materialData?.totalRatings || 0
        });
        try {
          const relatedRes = await API.get(`/materials?category=${encodeURIComponent(materialData.category || '')}`);
          const relatedList = Array.isArray(relatedRes.data) ? relatedRes.data : (relatedRes.data?.materials || []);
          setRelatedMaterials(relatedList.filter(item => item._id !== materialData._id).slice(0, 4));
        } catch (relatedErr) {
          setRelatedMaterials([]);
        }
        setReviews(reviewsData?.reviews || []);
        const minimum = Number(materialData?.minimumOrder || 1);
        setQuantity(minimum > 0 ? minimum : 1);
        setSelectedVariantIndex((materialData?.variants || []).filter(v => v?.size).length ? 0 : -1);
      })
      .catch(err => setError(err.response?.data?.message || 'Unable to load material'))
      .finally(() => setLoading(false));
  }, [id]);

  const images = useMemo(() => {
    const source = Array.isArray(material?.images) ? material.images.filter(Boolean) : [];
    return source.length ? source : [null, null];
  }, [material]);

  const owner = material?.owner;
  const distance = userLocation && owner?.location?.lat && owner?.location?.lng
    ? calcDistance(userLocation.lat, userLocation.lng, owner.location.lat, owner.location.lng)
    : null;
  const distanceLabel = distance === null
    ? 'Distance unavailable'
    : distance < 1 ? `${Math.round(distance * 1000)}m away` : `${distance.toFixed(1)}km away`;
  const deliveryLabel = distance === null ? 'Delivery time unavailable' : `${Math.max(1, Math.ceil(distance / 12))}-${Math.max(2, Math.ceil(distance / 12) + 1)} hrs`;
  const minOrder = Number(material?.minimumOrder || 1);
  const variants = Array.isArray(material?.variants) ? material.variants.filter(v => v?.size) : [];
  const selectedVariant = selectedVariantIndex >= 0 ? variants[selectedVariantIndex] : null;
  const displayPrice = Number(selectedVariant?.price || material?.price || 0);
  const displayStock = Number(selectedVariant ? selectedVariant.stock : material?.stock || 0);
  const displayUnit = selectedVariant?.unit || material?.unit || 'piece';
  const wishlisted = material ? isWishlisted(material._id) : false;

  const changeImage = (step) => {
    setActiveImage(prev => (prev + step + images.length) % images.length);
  };

  const handleAddToCart = () => {
    if (!user) { toast.info('Please login to add items'); navigate('/login'); return; }
    if (user.role !== 'customer') { toast.warning('Only customers can order'); return; }
    addToCart(material, owner, quantity, selectedVariant);
    toast.success(`${material.name}${selectedVariant ? ` - ${selectedVariant.size}` : ''} added to cart`);
  };

  const handleQuote = () => setShowQuote(true);

  const handleWishlist = () => {
    toggleWishlist(material, owner);
    toast.info(wishlisted ? 'Removed from wishlist' : 'Added to wishlist');
  };

  const handleReviewSubmit = async () => {
    if (!user) { toast.info('Please login to review this product'); navigate('/login'); return; }
    if (user.role !== 'customer') { toast.warning('Only customers can review products'); return; }
    if (!reviewRating) { toast.error('Please select a rating'); return; }
    setSubmittingReview(true);
    try {
      const res = await API.post(`/materials/${id}/rate`, {
        rating: reviewRating,
        comment: reviewComment
      });
      setReviews(res.data?.reviews || []);
      setMaterial(prev => ({
        ...prev,
        rating: res.data?.rating || 0,
        totalRatings: res.data?.totalRatings || 0
      }));
      setReviewRating(0);
      setReviewComment('');
      toast.success('Review submitted');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) return <div style={{ minHeight: '100vh', background: 'var(--page-bg)' }}><Navbar /><LoadingState label="Loading product..." /></div>;
  if (error || !material) return <div style={{ minHeight: '100vh', background: 'var(--page-bg)' }}><Navbar /><div style={{ maxWidth: 900, margin: '0 auto', padding: 24 }}><ErrorState message={error || 'Material not found'} onRetry={() => window.location.reload()} /></div></div>;

  return (
    <div className="material-detail-page page-with-bottom-nav">
      <Navbar />
      <main className="material-detail-shell">
        <div className="material-breadcrumb">
          <Link to="/shops">Products</Link>
          <span>/</span>
          <span>{material.name}</span>
        </div>

        <section className="material-detail-hero">
          <div className="material-gallery">
            <div className="material-gallery-main">
              <span className="gallery-count">{activeImage + 1} / {images.length}</span>
              <button className="gallery-nav gallery-prev" onClick={() => changeImage(-1)} aria-label="Previous image"><ChevronLeft size={24} /></button>
              <button className="gallery-nav gallery-next" onClick={() => changeImage(1)} aria-label="Next image"><ChevronRight size={24} /></button>
              {images[activeImage] ? (
                <img src={images[activeImage]} alt={material.name} />
              ) : (
                <div className="gallery-placeholder"><Package size={38} /><span>Product image</span></div>
              )}
            </div>
            <div className="material-gallery-thumbs">
              {images.map((image, index) => (
                <button key={index} className={activeImage === index ? 'active' : ''} onClick={() => setActiveImage(index)} aria-label={`Show image ${index + 1}`}>
                  {image ? <img src={image} alt="" /> : <span>Product image</span>}
                </button>
              ))}
            </div>
          </div>

          <div className="material-purchase-panel">
            <div className="material-brand-line">{material.brand || CATEGORY_LABELS[material.category] || 'Material'}</div>
            <h1>{material.name}</h1>
            <p className="material-unit-line">{displayUnit}</p>
            <div className="material-detail-price">Rs {displayPrice.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            <div className="material-rating-line">
              <Star size={15} fill="#F59E0B" color="#F59E0B" />
              <strong>{Number(material.rating || 0).toFixed(1)}</strong>
              <span>({material.totalRatings || 0} reviews)</span>
            </div>
            <div className={`material-stock-line ${displayStock > 0 ? 'in' : 'out'}`}>
              <CheckCircle size={18} /> {displayStock > 0 ? 'In Stock' : 'Out of Stock'}
            </div>

            <div className="material-info-box">
              <div>
                <span>Minimum order</span>
                <strong>{minOrder} {displayUnit}</strong>
              </div>
              <div>
                <span>Delivery</span>
                <strong>{deliveryLabel} / {distanceLabel}</strong>
              </div>
            </div>

            {(material.description || material.specifications?.some(spec => spec.value)) && (
              <div className="material-detail-specs">
                <div className="material-detail-specs-title">Product details</div>
                {material.description && <p>{material.description}</p>}
                {Array.isArray(material.specifications) && material.specifications.some(spec => spec.value) && (
                  <div className="material-detail-spec-list">
                    {material.specifications.filter(spec => spec.value).slice(0, 6).map((spec, index) => (
                      <span key={`${spec.key}-${index}`}>{spec.key}: {spec.value}</span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {variants.length > 0 && (
              <div className="material-variant-section">
                <div className="material-variant-title">Sizes / Variants ({variants.length})</div>
                <div className="material-variant-grid">
                  {variants.map((variant, index) => {
                    const isActive = selectedVariantIndex === index;
                    const stock = Number(variant.stock || 0);
                    return (
                      <button
                        key={`${variant.size}-${index}`}
                        type="button"
                        className={`material-variant-option ${isActive ? 'active' : ''}`}
                        onClick={() => setSelectedVariantIndex(index)}
                      >
                        <span className="material-variant-name">{variant.size}</span>
                        <span className="material-variant-meta">
                          Rs {Number(variant.price || 0).toLocaleString('en-IN')} / {variant.unit || material.unit || 'unit'}
                        </span>
                        <span className={stock > 0 ? 'material-variant-stock in' : 'material-variant-stock out'}>
                          {stock > 0 ? `${stock} available` : 'Out of stock'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="material-quantity-row">
              <strong>Quantity</strong>
              <div className="quantity-stepper">
                <button onClick={() => setQuantity(q => Math.max(minOrder || 1, q - 1))} aria-label="Decrease quantity"><Minus size={16} /></button>
                <span>{quantity}</span>
                <button onClick={() => setQuantity(q => q + 1)} aria-label="Increase quantity"><Plus size={18} /></button>
              </div>
            </div>

            <div className="material-detail-actions">
              <button className="material-detail-add" onClick={handleAddToCart} disabled={displayStock === 0}>
                <ShoppingCart size={18} /> Add to Cart
              </button>
              <button className="material-detail-quote" onClick={handleQuote}>
                <FileText size={18} /> Request Quote
              </button>
              <button className={`material-detail-wish ${wishlisted ? 'active' : ''}`} onClick={handleWishlist} aria-label="Wishlist">
                <Heart size={18} fill={wishlisted ? 'currentColor' : 'none'} />
              </button>
            </div>
          </div>
        </section>

        <section className="material-seller-section">
          <h2>Sold by</h2>
          <Link to={`/shops/${owner?._id}`} className="material-seller-card">
            <div className="seller-title-row">
              <div className="seller-avatar"><Store size={20} /></div>
              <div>
                <h3>{owner?.shopName || 'Supplier'}</h3>
                <p>{owner?.location?.city}, {owner?.location?.state}</p>
              </div>
              <span className="seller-gst"><ShieldCheck size={13} /> GST</span>
            </div>
            <div className="seller-meta-row">
              <span><MapPin size={15} /> {distanceLabel}</span>
              <span><Star size={15} /> {(owner?.rating || 0).toFixed(1)}</span>
            </div>
            <div className="seller-meta-row">
              <span>{deliveryLabel}</span>
            </div>
          </Link>
        </section>

        {relatedMaterials.length > 0 && (
          <section className="material-related-section">
            <div className="material-related-header">
              <div>
                <span>More to explore</span>
                <h2>Related products</h2>
              </div>
              <Link to={`/shops?view=materials&category=${material.category}`}>
                View all <ArrowRight size={16} />
              </Link>
            </div>
            <div className="material-related-grid">
              {relatedMaterials.map(item => {
                const image = Array.isArray(item.images) ? item.images.find(Boolean) : null;
                const itemOwner = item.owner || {};
                return (
                  <Link to={`/materials/${item._id}`} className="material-related-card" key={item._id}>
                    <div className="material-related-image">
                      {image ? <img src={image} alt={item.name} /> : <Package size={30} />}
                    </div>
                    <div className="material-related-body">
                      <span className="material-related-brand">{item.brand || CATEGORY_LABELS[item.category] || 'Material'}</span>
                      <h3>{item.name}</h3>
                      <p>{itemOwner.shopName || 'Verified supplier'}</p>
                      <div>
                        <strong>Rs {Number(item.price || 0).toLocaleString('en-IN')}</strong>
                        <span>{Number(item.stock || 0) > 0 ? 'In stock' : 'Out of stock'}</span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        <section className="material-review-section">
          <div className="material-review-header">
            <h2>Product Reviews</h2>
            <span className="material-review-summary">
              <Star size={14} fill="#F59E0B" color="#F59E0B" />
              {Number(material.rating || 0).toFixed(1)} / 5
            </span>
          </div>

          <div className="material-review-form">
            <div className="material-star-picker" aria-label="Select rating">
              {[1, 2, 3, 4, 5].map(star => (
                <button
                  key={star}
                  type="button"
                  className={star <= reviewRating ? 'active' : ''}
                  onClick={() => setReviewRating(star)}
                  aria-label={`${star} star rating`}
                >
                  <Star size={18} fill={star <= reviewRating ? 'currentColor' : 'none'} />
                </button>
              ))}
            </div>
            <textarea
              value={reviewComment}
              onChange={(e) => setReviewComment(e.target.value)}
              maxLength={500}
              placeholder="Write your review"
            />
            <div className="material-review-form-actions">
              <button className="material-review-submit" type="button" onClick={handleReviewSubmit} disabled={submittingReview}>
                {submittingReview ? 'Submitting...' : 'Submit Review'}
              </button>
            </div>
          </div>

          <div className="material-review-list">
            {reviews.length === 0 ? (
              <div className="material-review-empty">No reviews yet. Be the first customer to rate this product.</div>
            ) : reviews.map(review => (
              <article className="material-review-item" key={review._id || `${review.userName}-${review.date}`}>
                <div className="material-review-item-top">
                  <span className="material-review-user">{review.userName || 'Customer'}</span>
                  <span className="material-review-stars">
                    {'★'.repeat(Number(review.rating || 0))}{'☆'.repeat(5 - Number(review.rating || 0))}
                  </span>
                </div>
                {review.comment && <p className="material-review-comment">{review.comment}</p>}
                {review.date && <div className="material-review-date">{new Date(review.date).toLocaleDateString('en-IN')}</div>}
              </article>
            ))}
          </div>
        </section>
        {showQuote && <QuoteRequestModal material={material} selectedVariant={selectedVariant} onClose={() => setShowQuote(false)} />}
      </main>
    </div>
  );
}
